import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Controls the open/closed state of the Cadastro de Clínicas listing modal
 * (Cadastros → "Clínicas", TASK-60770).
 *
 * The modal is an OVERLAY rendered on top of whatever page is currently displayed — it is NOT a
 * route. The sidebar leaf, the mobile drawer and the home quick-access tile toggle it through this
 * singleton, and `LayoutComponent` (present on every page) hosts the modal component, mounting it
 * only while `open$` is `true`. No navigation happens when the modal opens or closes, so the page
 * behind it stays intact and visible (dimmed) under the scrim.
 *
 * Close-on-nav: the `LayoutComponent` host is re-created on every navigation. If the user
 * navigates while the overlay is open, the freshly mounted layout would re-mount a SECOND modal
 * instance and reset the grid to page 1. To prevent that, this singleton (which outlives every
 * LayoutComponent) closes the overlay on `NavigationStart`, so the new layout always mounts with
 * `open$ === false`.
 *
 * Mold: `HealthFacilitiesModalService` (TASK-60341).
 */
@Injectable({
  providedIn: 'root'
})
export class ClinicsModalService {
  private readonly openState$ = new BehaviorSubject<boolean>(false);

  /** Emits the current open state; replays the latest value to new subscribers. */
  readonly open$: Observable<boolean> = this.openState$.asObservable();

  constructor(private readonly router: Router) {
    // The subscription lives for the app lifetime — this is a root singleton, so there is nothing
    // to unsubscribe from.
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.close());
  }

  /** Opens the modal overlay (mounts the component via the layout host). */
  open(): void {
    this.openState$.next(true);
  }

  /** Closes the modal overlay (unmounts the component). No navigation. */
  close(): void {
    this.openState$.next(false);
  }
}
